import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, FileCheck2, Users, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Shield,
    title: "Cybersecurity",
    description:
      "Practical cybersecurity strategies informed by experienced security professionals, military leadership, and disciplined program execution.",
    features: ["Threat Assessment", "Security Architecture", "Risk Reduction"],
    gradient: "from-blue-500/20 to-cyan-500/10",
  },
  {
    icon: FileCheck2,
    title: "CMMC Compliance",
    description:
      "Help organizations understand CMMC requirements, identify security gaps, prepare documentation, and develop remediation strategies.",
    features: ["CMMC Readiness", "Gap Assessment", "Remediation Planning"],
    gradient: "from-[#D4AF37]/20 to-amber-500/10",
  },
  {
    icon: Users,
    title: "Cybersecurity & Strategic Advisory",
    description:
      "Strategic guidance informed by 25+ years of military leadership, program management, and cybersecurity experience.",
    features: ["Cybersecurity Strategy", "Program Management", "Risk Advisory"],
    gradient: "from-emerald-500/20 to-teal-500/10",
  },
];

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/30 to-slate-950" />
      
      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
            Comprehensive Security Solutions
          </h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
            From cybersecurity strategy to CMMC compliance, we provide end-to-end 
            solutions to strengthen your security posture.
          </p>
        </motion.div>

        {/* Services Grid - Bento Style */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="service-card h-full p-8 rounded-2xl flex flex-col">
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                  
                  {/* Content */}
                  <div className="relative z-10 flex-1">
                    {/* Icon & Arrow */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-14 h-14 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:border-[#D4AF37]/50 transition-colors">
                        <Icon className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-800/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                    </div>

                    {/* Title & Description */}
                    <h3 className="text-white text-xl font-bold mb-3 group-hover:text-[#D4AF37] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="mt-auto">
                      <div className="flex flex-wrap gap-2">
                        {service.features.map((feature) => (
                          <span
                            key={feature}
                            className="px-3 py-1 text-xs font-medium text-slate-400 bg-slate-800/50 rounded-full border border-slate-700/50"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12 relative z-10"
        >
          <p className="text-slate-400 mb-4">
            Need a custom solution for your organization?
          </p>
          <a
            href="https://calendly.com/first-defense/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#D4AF37] font-semibold hover:underline underline-offset-4 transition-all inline-block py-2 px-4"
            data-testid="services-contact-link"
          >
            Let&apos;s discuss your requirements &rarr;
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
